
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppSettingsService } from '../../../services/app-settings.service';
import { BuildInfo, formatBuildInfo, normalizeBuildInfo } from '../../../models/build-info.model';

type BuildProbeState = 'loading' | 'ok' | 'no-build-info' | 'unavailable';

interface BuildServiceStatus {
  label: string;
  build: BuildInfo | null;
  state: BuildProbeState;
}

@Component({
    selector: 'app-build-stamp',
    imports: [],
    template: `
    <div class="build-stamp" [class.open]="isOpen">
      <button
        type="button"
        class="build-stamp-trigger"
        [attr.aria-expanded]="isOpen"
        aria-label="Show build details"
        (click)="toggle()"
        >
        {{ displayVersion }}
      </button>
    
      @if (isOpen) {
        <section class="build-stamp-panel" aria-label="Build details">
          <div class="build-stamp-row">
            <span>Portal</span>
            <strong>{{ format(portalBuild) }}</strong>
          </div>
          @for (service of services; track service) {
            <div class="build-stamp-row">
              <span>{{ service.label }}</span>
              <strong [class.muted]="service.state !== 'ok'">
                {{ describe(service) }}
              </strong>
            </div>
          }
        </section>
      }
    </div>
    `,
    styles: [`
    .build-stamp {
      position: fixed;
      left: 0.75rem;
      bottom: 0.75rem;
      z-index: 9998;
      font-size: 0.6875rem;
      color: #d1d5db;
      max-width: calc(100vw - 1.5rem);
      pointer-events: none;
    }

    /* Phones/small tablets: the stamp overlaps page-level composers and
       toolbars and is a dev/support affordance, not end-user UI. */
    @media (max-width: 768px) {
      .build-stamp {
        display: none;
      }
    }

    .build-stamp-trigger {
      pointer-events: auto;
      border: 1px solid rgba(255, 255, 255, 0.16);
      background: rgba(17, 24, 39, 0.86);
      color: inherit;
      border-radius: 6px;
      padding: 0.25rem 0.45rem;
      font: inherit;
      line-height: 1.2;
      max-width: min(23rem, calc(100vw - 1.5rem));
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(15, 23, 42, 0.22);
    }

    .build-stamp-panel {
      pointer-events: auto;
      width: min(25rem, calc(100vw - 1.5rem));
      margin-top: 0.35rem;
      padding: 0.55rem;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.14);
      background: rgba(17, 24, 39, 0.96);
      box-shadow: 0 10px 26px rgba(15, 23, 42, 0.3);
    }

    .build-stamp-row {
      display: grid;
      grid-template-columns: 5.5rem minmax(0, 1fr);
      gap: 0.5rem;
      align-items: baseline;
      padding: 0.25rem 0;
    }

    .build-stamp-row span {
      color: #9ca3af;
    }

    .build-stamp-row strong {
      color: #f9fafb;
      font-weight: 500;
      overflow-wrap: anywhere;
    }

    .build-stamp-row strong.muted {
      color: #9ca3af;
      font-weight: 400;
    }
  `]
})
export class BuildStampComponent implements OnInit {
  portalBuild: BuildInfo | null = null;
  services: BuildServiceStatus[] = [];
  isOpen = false;

  constructor(
    private appSettings: AppSettingsService,
    private http: HttpClient
  ) {}

  get displayVersion(): string {
    return formatBuildInfo(this.portalBuild);
  }

  ngOnInit(): void {
    const settings = this.appSettings.getSettings();
    this.portalBuild = normalizeBuildInfo(settings.build) ?? {
      productName: 'Solo',
      productVersion: '1.0.0',
      buildStamp: 'local',
      component: 'tenant-portal',
      environment: settings.environment || 'local'
    };

    this.services = [
      { label: 'Tenant API', build: null, state: 'loading' },
      { label: 'Chat API', build: null, state: 'loading' }
    ];

    void this.loadServiceBuild(0, `${settings.apiUrl.replace(/\/+$/, '')}/build`);

    const chatHealthUrl = this.getChatHealthUrl(settings.chatUrl);
    if (chatHealthUrl) {
      void this.loadServiceHealth(1, chatHealthUrl);
    } else {
      this.services[1] = { ...this.services[1], state: 'unavailable' };
    }
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  format(build: BuildInfo | null): string {
    return formatBuildInfo(build);
  }

  describe(service: BuildServiceStatus): string {
    switch (service.state) {
      case 'loading':
        return '…';
      case 'ok':
        return this.format(service.build);
      case 'no-build-info':
        return 'Online · no build info';
      default:
        return 'Unavailable';
    }
  }

  private async loadServiceBuild(index: number, url: string): Promise<void> {
    try {
      const value = await firstValueFrom(this.http.get<unknown>(url));
      this.services[index] = {
        ...this.services[index],
        build: normalizeBuildInfo(value),
        state: 'ok'
      };
    } catch (error) {
      // An HTTP status (404/405/401/...) means the host answered: the service is
      // up but exposes no build metadata.
      // Only status 0 (network failure, DNS, or CORS-blocked response) means we
      // genuinely could not reach it. Keep "Unavailable" for that case alone.
      const answered = error instanceof HttpErrorResponse && error.status > 0;
      this.services[index] = {
        ...this.services[index],
        build: null,
        state: answered ? 'no-build-info' : 'unavailable'
      };
    }
  }

  private async loadServiceHealth(index: number, url: string): Promise<void> {
    try {
      // Chat exposes /health, not /build. A health body is not build metadata.
      await firstValueFrom(this.http.get<unknown>(url));
      this.services[index] = {
        ...this.services[index], build: null, state: 'no-build-info'
      };
    } catch (error) {
      const answered = error instanceof HttpErrorResponse && error.status > 0;
      this.services[index] = {
        ...this.services[index], build: null,
        state: answered ? 'no-build-info' : 'unavailable'
      };
    }
  }

  private getChatHealthUrl(chatUrl: string | undefined): string | null {
    if (!chatUrl) {
      return null;
    }

    try {
      const url = new URL(chatUrl);
      url.pathname = '/health';
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return null;
    }
  }
}
